import React, { useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * StagingUploadModal — CSV upload dialog for the staging area.
 * Uploads to /api/staging/upload, shows results, then refreshes parent.
 */
export default function StagingUploadModal({ open, onClose, getAuthHeader, onUploadComplete }) {
    const [file, setFile] = useState(null);
    const [listName, setListName] = useState('');
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState(null);

    const handleUpload = async () => {
        if (!file) return;
        setUploading(true);
        setResult(null);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('list_name', listName || file.name.replace(/\.csv$/i, ''));

            const res = await axios.post(`${API}/staging/upload`, formData, {
                headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
            });

            setResult(res.data);
            toast.success(`Uploaded ${res.data.valid_rows} records${res.data.assigned ? ` (${res.data.assigned} assigned)` : ''}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Upload failed');
        } finally {
            setUploading(false);
        }
    };

    const handleClose = () => {
        if (result) {
            onUploadComplete();
        }
        setFile(null);
        setListName('');
        setResult(null);
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>Upload CSV to Staging</DialogTitle>
                    <DialogDescription>
                        Upload a CSV with contact and company data. Records will be staged for review before committing to the CRM.
                    </DialogDescription>
                </DialogHeader>

                {!result ? (
                    <>
                        <div className="space-y-4 py-2">
                            <div>
                                <Label>List Name</Label>
                                <Input
                                    value={listName}
                                    onChange={(e) => setListName(e.target.value)}
                                    placeholder="e.g., PitchBook Q2 2026"
                                    className="mt-1"
                                />
                            </div>

                            <div>
                                <Label>CSV File</Label>
                                <label className="mt-1 flex flex-col items-center justify-center border-2 border-dashed border-stone-300 rounded-lg p-6 cursor-pointer hover:border-stone-400 hover:bg-stone-50 transition-colors">
                                    {file ? (
                                        <div className="flex items-center gap-2 text-stone-700">
                                            <FileSpreadsheet className="w-5 h-5 text-green-600" />
                                            <span className="text-sm font-medium">{file.name}</span>
                                            <span className="text-xs text-stone-400">({(file.size / 1024).toFixed(1)} KB)</span>
                                        </div>
                                    ) : (
                                        <>
                                            <Upload className="w-8 h-8 text-stone-400 mb-2" />
                                            <span className="text-sm text-stone-500">Click to select CSV file</span>
                                        </>
                                    )}
                                    <input
                                        type="file"
                                        accept=".csv"
                                        className="hidden"
                                        onChange={(e) => setFile(e.target.files[0])}
                                    />
                                </label>
                            </div>

                            <div className="bg-stone-50 rounded-lg p-3 text-xs text-stone-500 space-y-1">
                                <p className="font-medium text-stone-600">Supported columns:</p>
                                <p><strong>Contact:</strong> First Name, Last Name, Personal Email, Mobile/WhatsApp, City, County, Personal LinkedIn URL, Job Title, Department, Seniority, Role in Deal, Relationship, Sales Rep</p>
                                <p><strong>Company:</strong> Company Name, Company Size, Company Email, Company Phone, Company City, Company State/Province, Company Country, Company HQ Country, Company LinkedIn URL, Company Website</p>
                            </div>
                        </div>

                        <DialogFooter>
                            <Button variant="outline" onClick={handleClose}>Cancel</Button>
                            <Button
                                onClick={handleUpload}
                                disabled={!file || uploading}
                                className="bg-stone-900 hover:bg-stone-800"
                            >
                                {uploading ? (
                                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</>
                                ) : (
                                    <><Upload className="w-4 h-4 mr-2" />Upload & Stage</>
                                )}
                            </Button>
                        </DialogFooter>
                    </>
                ) : (
                    <>
                        {/* Upload result summary */}
                        <div className="space-y-3 py-2">
                            <div className="flex items-center gap-3">
                                <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                                    <span className="text-sm font-medium text-green-800">{result.valid_rows} staged</span>
                                </div>
                                {result.assigned > 0 && (
                                    <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                                        <CheckCircle2 className="w-4 h-4 text-blue-600" />
                                        <span className="text-sm font-medium text-blue-800">{result.assigned} auto-assigned</span>
                                    </div>
                                )}
                                {result.skipped_rows > 0 && (
                                    <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                                        <AlertTriangle className="w-4 h-4 text-amber-600" />
                                        <span className="text-sm font-medium text-amber-800">{result.skipped_rows} skipped</span>
                                    </div>
                                )}
                            </div>

                            {result.unmatched_reps?.length > 0 && (
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                                    <strong>Unmatched reps:</strong> {result.unmatched_reps.join(', ')}
                                    <p className="text-xs text-amber-600 mt-1">These records were staged but not assigned. You can assign them manually.</p>
                                </div>
                            )}

                            {result.mapped_columns && (
                                <div className="text-xs text-stone-500">
                                    <strong>Mapped columns:</strong> {Object.keys(result.mapped_columns).join(', ')}
                                </div>
                            )}
                        </div>

                        <DialogFooter>
                            <Button onClick={handleClose} className="bg-stone-900 hover:bg-stone-800">
                                View Staged Records
                            </Button>
                        </DialogFooter>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
